import React, { useMemo, useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Box,
  Card,
  Button,
  IconButton,
  FormControl,
  OutlinedInput,
  InputAdornment,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Autocomplete,
  TableBody,
} from "@mui/material";
import { useFormik } from "formik";
import { uploadImage } from "../../Components/UploadFile/upload";
import { useStyles } from "../../theme/appStyles";
import { useNotifier } from "../../Core/Notifier";
import {
  ICompanyDetails,
  punchSlideItems,
} from "../../Api/Interface/api.interface";
import {
  useCreateSettingMutation,
  useGetSettingQuery,
} from "../../Api/attoDeskApi";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import {
  formatPhoneNumberForDisplay,
  validatePhoneNumber,
} from "../../Components/Validation/validationUtils";
import { toProperCase } from "../../Components/Validation/toProperCase";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { FaTrash } from "react-icons/fa";
import { punchSliderItems } from "../../Core/Enum/enum";

const toAllCaps = (text: string | null | undefined): string => {
  if (!text) return "";
  return text.toUpperCase();
};

const Index = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { showErrorMessage, showMessage } = useNotifier();
  const [selectedImage, setSelectedImage] = useState<File | undefined>(
    undefined
  );
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [selectedImageUp, setSelectedImageUp] = useState<File | undefined>(
    undefined
  );
  const [isImageUploading2, setIsImageUploadingUp] = useState(false);

  const [selectedImagePunch, setSelectedImagePunch] = useState<
    File | undefined
  >(undefined);
  const [isImageUploadingPunch, setIsImageUploadingPunch] = useState(false);

  const [punchSlideItems, setPunchSlideItems] = useState<punchSlideItems[]>([]);

  const { data, isLoading, isError } = useGetSettingQuery({
    searchText: "",
    id: "1",
  });

  const [settingData, { isLoading: settingLoading }] =
    useCreateSettingMutation();
  const [initialData, setInitialData] = useState<ICompanyDetails | null>(null);

  useEffect(() => {
    if (data?.data) {
      setInitialData(data.data as ICompanyDetails);
    }
  }, [data]);

  useEffect(() => {
    if (initialData && Array.isArray(initialData.punch_slider_items)) {
      setPunchSlideItems(initialData.punch_slider_items);
    } else {
      setPunchSlideItems([]);
    }
  }, [initialData]);

  const handlePhoneChange = (
    fieldName: string,
    value: string,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const formattedValue = formatPhoneNumberForDisplay(value);
    setFieldValue(fieldName, formattedValue);
  };

  const formik = useFormik({
    initialValues: {
      companyName: initialData?.companyName || "",
      companyProvince: initialData?.companyProvince || "",
      companyCity: initialData?.companyCity || "",
      companyPostalCode: initialData?.companyPostalCode || "",
      companyStreetNumber: initialData?.companyStreetNumber || "",
      companyStreetName: initialData?.companyStreetName || "",
      companyPaymentMethod: initialData?.companyPaymentMethod || "",
      companyEmail: initialData?.companyEmail || "",

      companyMobileNumber: initialData?.companyMobileNumber
        ? formatPhoneNumberForDisplay(initialData.companyMobileNumber)
        : "",

      companyAltMobileNumber: initialData?.companyAltMobileNumber
        ? formatPhoneNumberForDisplay(initialData.companyAltMobileNumber)
        : "",
      companyWebSiteLink: initialData?.companyWebSiteLink || "",
      companyDescription: initialData?.companyDescription || "",
      companyLogo: initialData?.companyLogo || "",
      logowidthPrint: initialData?.logowidthPrint || "",
      backgroundImg: initialData?.backgroundImg || "",
      punch_login_background_color:
        initialData?.punch_login_background_color || "",
      punch_login_background_url: initialData?.punch_login_background_url || "",
      punch_login_font_color: initialData?.punch_login_font_color || "",
      punch_slider_items: initialData?.punch_slider_items || [],
      calculate_radius: initialData?.calculate_radius || "",
      company_latitude: initialData?.company_latitude || "",
      company_longitude: initialData?.company_longitude || "",
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: { [key: string]: string } = {};

      const companyMobileNumberError = validatePhoneNumber(
        values.companyMobileNumber
      );
      if (companyMobileNumberError) {
        errors.companyMobileNumber = companyMobileNumberError;
      }

      const companyAltMobileNumberError = validatePhoneNumber(
        values.companyAltMobileNumber
      );
      if (companyAltMobileNumberError) {
        errors.companyAltMobileNumber = companyAltMobileNumberError;
      }

      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const formData = new FormData();
        let companyLogo: string | undefined = initialData?.companyLogo;

        if (selectedImage) {
          setIsImageUploading(true);
          formData.append("companyLogo", selectedImage);
          companyLogo = (await uploadImage(selectedImage)) || "";
          setIsImageUploading(false);
        }

        const formDataUp = new FormData();
        let backgroundImg: string | undefined = initialData?.backgroundImg;

        if (selectedImageUp) {
          setIsImageUploadingUp(true);
          formDataUp.append("backgroundImg", selectedImageUp);
          backgroundImg = (await uploadImage(selectedImageUp)) || "";
          setIsImageUploadingUp(false);
        }

        const formDataPunch = new FormData();
        let punch_login_background_url: string | undefined =
          initialData?.punch_login_background_url;

        if (selectedImagePunch) {
          setIsImageUploadingPunch(true);
          formDataPunch.append(
            "punch_login_background_url",
            selectedImagePunch
          );
          punch_login_background_url =
            (await uploadImage(selectedImagePunch)) || "";
          setIsImageUploadingPunch(false);
        }

        const updatedData = {
          id: initialData?.id,
          companyName: toProperCase(values.companyName),
          companyProvince: toProperCase(values.companyProvince),
          companyCity: toProperCase(values.companyCity),
          companyPostalCode: toAllCaps(values.companyPostalCode),
          companyStreetNumber: values.companyStreetNumber,
          companyStreetName: toProperCase(values.companyStreetName),
          companyPaymentMethod: values.companyPaymentMethod,
          companyEmail: values.companyEmail,
          companyMobileNumber: values.companyMobileNumber.replace(/-/g, ""),
          companyAltMobileNumber: values.companyAltMobileNumber.replace(
            /-/g,
            ""
          ),
          companyWebSiteLink: values.companyWebSiteLink,
          companyDescription: values.companyDescription,
          companyLogo,
          logowidthPrint: values.logowidthPrint,
          backgroundImg,
          punch_login_background_color: values.punch_login_background_color,
          punch_login_font_color: values.punch_login_font_color,
          punch_login_background_url,
          punch_slider_items: punchSlideItems,
          calculate_radius: values.calculate_radius,
          company_latitude: values.company_latitude,
          company_longitude: values.company_longitude,
        };

        const response = await settingData(updatedData).unwrap();
        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          const tokenData = response.data as ICompanyDetails;
          if (tokenData) {
            localStorage.setItem("backgroundImg", tokenData.backgroundImg);
            localStorage.setItem("companyLogo", tokenData.companyLogo);
            window.location.reload();
          }
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formValid = useMemo(() => {
    return formik.values.companyName === "" ||
      formik.values.companyName === undefined
      ? false
      : true;
  }, [formik]);

  const resizeImage = (
    file: File,
    maxWidth: number,
    maxHeight: number
  ): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          let width = img.width;
          let height = img.height;

          // Calculate the new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
              });
              resolve(resizedFile);
            } else {
              reject(new Error("Canvas is empty"));
            }
          }, file.type);
        };

        img.onerror = (error) => reject(error);
      };
    });
  };

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;

    if (files && files.length > 0) {
      const file = files[0];
      const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
      const maxSize = 10 * 1024 * 1024;

      if (!validTypes.includes(file.type)) {
        showErrorMessage("The file must be an image (jpeg, png, jpg, gif).");
        setSelectedImage(undefined);
      } else if (file.size > maxSize) {
        showErrorMessage("The file must be smaller than 10 MB.");
        setSelectedImage(undefined);
      } else {
        try {
          const resizedImage = await resizeImage(file, 500, 500);
          setSelectedImage(resizedImage);
        } catch (error) {
          showErrorMessage("Failed to resize the image.");
          setSelectedImage(undefined);
        }
      }
    } else {
      setSelectedImage(undefined);
    }
  };

  const resizeImageUp = (
    file: File,
    maxWidth: number,
    maxHeight: number
  ): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          let width = img.width;
          let height = img.height;

          // Calculate the new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            if (blob) {
              const resizedFile2 = new File([blob], file.name, {
                type: file.type,
              });
              resolve(resizedFile2);
            } else {
              reject(new Error("Canvas is empty"));
            }
          }, file.type);
        };

        img.onerror = (error) => reject(error);
      };
    });
  };

  const handleImageChange2 = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;

    if (files && files.length > 0) {
      const file = files[0];
      const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
      const maxSize = 10 * 1024 * 1024;

      if (!validTypes.includes(file.type)) {
        showErrorMessage("The file must be an image (jpeg, png, jpg, gif).");
        setSelectedImageUp(undefined);
      } else if (file.size > maxSize) {
        showErrorMessage("The file must be smaller than 10 MB.");
        setSelectedImageUp(undefined);
      } else {
        try {
          const resizeImageJJ = await resizeImageUp(file, 500, 500);
          setSelectedImageUp(resizeImageJJ);
        } catch (error) {
          showErrorMessage("Failed to resize the image.");
          setSelectedImageUp(undefined);
        }
      }
    } else {
      setSelectedImageUp(undefined);
    }
  };
  const resizeImagePunch = (
    file: File,
    maxWidth: number,
    maxHeight: number
  ): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          let width = img.width;
          let height = img.height;

          // Calculate the new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            if (blob) {
              const resizedFile2 = new File([blob], file.name, {
                type: file.type,
              });
              resolve(resizedFile2);
            } else {
              reject(new Error("Canvas is empty"));
            }
          }, file.type);
        };

        img.onerror = (error) => reject(error);
      };
    });
  };

  const handleImageChangePunch = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;

    if (files && files.length > 0) {
      const file = files[0];
      const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
      const maxSize = 10 * 1024 * 1024;

      if (!validTypes.includes(file.type)) {
        showErrorMessage("The file must be an image (jpeg, png, jpg, gif).");
        setSelectedImagePunch(undefined);
      } else if (file.size > maxSize) {
        showErrorMessage("The file must be smaller than 10 MB.");
        setSelectedImagePunch(undefined);
      } else {
        try {
          const resizeImageJJ = await resizeImagePunch(file, 500, 500);
          setSelectedImagePunch(resizeImageJJ);
        } catch (error) {
          showErrorMessage("Failed to resize the image.");
          setSelectedImagePunch(undefined);
        }
      }
    } else {
      setSelectedImagePunch(undefined);
    }
  };

  const handleDeleteRowPB = (index: number) => {
    const updatedRows = punchSlideItems.filter((_, i) => i !== index);
    setPunchSlideItems(updatedRows);
  };

  const handleFieldChangePB = <K extends keyof punchSlideItems>(
    index: number,
    field: K,
    value: punchSlideItems[K]
  ) => {
    const updatedRows = punchSlideItems.map((term, i) => {
      if (i === index) {
        return {
          ...term,
          [field]: value !== undefined ? value : term[field],
        };
      }
      return term;
    });

    setPunchSlideItems(updatedRows);
  };

  const handleFieldChange = (
    index: number,
    field: keyof punchSlideItems,
    value: any
  ) => {
    setPunchSlideItems((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
      return updated;
    });
  };

  const handleFieldChangeImg = async (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
    const maxSize = 10 * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      alert("The file must be an image (jpeg, png, jpg, gif).");
      return;
    }

    if (file.size > maxSize) {
      alert("The file must be smaller than 10 MB.");
      return;
    }

    const uploadedUrl = await uploadImage(file);
    if (uploadedUrl) {
      handleFieldChange(index, "url", uploadedUrl);
    }
  };

  const handleAddRowPB = () => {
    setPunchSlideItems((prev) => [
      ...prev,
      {
        type: "",
        url: "",
      },
    ]);
  };

  if (isLoading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="10vh"
      >
        <CircularProgress />
      </Box>
    );

  return (
    <Grid container spacing={3}>
      <Grid item lg={12} md={12} sm={12} xs={12}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ borderBottom: 2, padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Typography className={classes.cardTitle}>
                      Company Details
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={3} p={"24px"}>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">Name</Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Name"
                          size="small"
                          {...formik.getFieldProps("companyName")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                          Website Link
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Website Link"
                          size="small"
                          {...formik.getFieldProps("companyWebSiteLink")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">Description</Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Description"
                          size="small"
                          {...formik.getFieldProps("companyDescription")}
                          className={classes.discriptionTextField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          multiline
                          rows={8}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ borderBottom: 2, padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Typography className={classes.cardTitle}>
                      Contact Details
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={3} p={"24px"}>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                          Mobile Number
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Mobile Number"
                          size="small"
                          value={formik.values.companyMobileNumber}
                          onChange={(e) =>
                            handlePhoneChange(
                              "companyMobileNumber",
                              e.target.value,
                              formik.setFieldValue
                            )
                          }
                          onBlur={formik.handleBlur}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          error={
                            formik.touched.companyMobileNumber &&
                            Boolean(formik.errors.companyMobileNumber)
                          }
                          helperText={
                            formik.touched.companyMobileNumber &&
                            formik.errors.companyMobileNumber
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                          Alt Mobile Number
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Alt Mobile Number"
                          size="small"
                          value={formik.values.companyAltMobileNumber}
                          onChange={(e) =>
                            handlePhoneChange(
                              "companyAltMobileNumber",
                              e.target.value,
                              formik.setFieldValue
                            )
                          }
                          onBlur={formik.handleBlur}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          error={
                            formik.touched.companyAltMobileNumber &&
                            Boolean(formik.errors.companyAltMobileNumber)
                          }
                          helperText={
                            formik.touched.companyAltMobileNumber &&
                            formik.errors.companyAltMobileNumber
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">Email</Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Email"
                          size="small"
                          {...formik.getFieldProps("companyEmail")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ borderBottom: 2, padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Typography className={classes.cardTitle}>
                      Address
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={3} p={"24px"}>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">Street Name</Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Street Name"
                          size="small"
                          {...formik.getFieldProps("companyStreetName")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item lg={3} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                          Street Number
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Street Number"
                          size="small"
                          {...formik.getFieldProps("companyStreetNumber")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item lg={3} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">Postal Code</Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Postal Code"
                          size="small"
                          {...formik.getFieldProps("companyPostalCode")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">Province</Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Province"
                          size="small"
                          {...formik.getFieldProps("companyProvince")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">City</Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="City"
                          size="small"
                          {...formik.getFieldProps("companyCity")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ borderBottom: 2, padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Typography className={classes.cardTitle}>
                      Add Logo
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={2} p={"24px"}>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Box className={classes.imgBox}>
                      {selectedImage && (
                        <img
                          src={URL.createObjectURL(selectedImage)}
                          alt="Uploaded"
                          className={classes.SelectImageBox}
                        />
                      )}
                      {!selectedImage &&
                      initialData &&
                      initialData.companyLogo ? (
                        <img
                          alt="Selected"
                          height="160px"
                          src={initialData.companyLogo}
                          style={{ position: "inherit" }}
                          className={classes.SelectImageBox}
                        />
                      ) : (
                        <></>
                      )}
                      <div>
                        <input
                          accept="image/*"
                          style={{ display: "none" }}
                          id="icon-button-file-1"
                          type="file"
                          onChange={handleImageChange}
                        />
                        <div
                          style={{
                            textAlign: "center",
                          }}
                        >
                          <label htmlFor="icon-button-file-1">
                            <IconButton
                              color="primary"
                              aria-label="upload picture"
                              component="span"
                            >
                              <CloudUploadIcon sx={{ fontSize: 45 }} />
                            </IconButton>
                          </label>
                          <div>PNG, JPG and JPEG are allowed</div>
                        </div>
                      </div>
                    </Box>
                  </Grid>

                  <Grid item lg={4} md={4} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                          Logo print width
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Logo print width"
                          size="small"
                          {...formik.getFieldProps("logowidthPrint")}
                          className={classes.textField}
                          type="number"
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ borderBottom: 2, padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Typography className={classes.cardTitle}>
                      Add Background Image
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={2} p={"24px"}>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Box className={classes.imgBox}>
                      {selectedImageUp && (
                        <img
                          src={URL.createObjectURL(selectedImageUp)}
                          alt="Uploaded img"
                          className={classes.SelectImageBox}
                        />
                      )}
                      {!selectedImageUp &&
                      initialData &&
                      initialData.backgroundImg ? (
                        <img
                          alt="Selected img"
                          height="160px"
                          src={initialData.backgroundImg}
                          style={{ position: "inherit" }}
                          className={classes.SelectImageBox}
                        />
                      ) : (
                        <></>
                      )}
                      <div>
                        <input
                          accept="image/*"
                          style={{ display: "none" }}
                          id="icon-button-file-up-bg"
                          type="file"
                          onChange={handleImageChange2}
                        />
                        <div
                          style={{
                            textAlign: "center",
                          }}
                        >
                          <label htmlFor="icon-button-file-up-bg">
                            <IconButton
                              color="primary"
                              aria-label="upload picture up bg"
                              component="span"
                            >
                              <CloudUploadIcon sx={{ fontSize: 45 }} />
                            </IconButton>
                          </label>
                          <div>PNG, JPG and JPEG are allowed</div>
                        </div>
                      </div>
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ borderBottom: 2, padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Typography className={classes.cardTitle}>
                      Punch Login Setting
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={2} p={"24px"}>
                  <Grid item lg={3} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                          Background color
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <FormControl
                          size="small"
                          variant="outlined"
                          sx={{
                            width: 200,
                            "& .MuiOutlinedInput-root": {
                              paddingRight: "8px",
                              fontSize: 14,
                            },
                            '& input[type="color"]': {
                              width: 24,
                              height: 24,
                              border: "none",
                              padding: 0,
                              background: "transparent",
                              cursor: "pointer",
                            },
                          }}
                        >
                          <OutlinedInput
                            id="outlined-adornment-color"
                            value={formik.values.punch_login_background_color}
                            onChange={(e) =>
                              formik.setFieldValue(
                                "punch_login_background_color",
                                e.target.value
                              )
                            }
                            endAdornment={
                              <InputAdornment position="end" sx={{ ml: 1 }}>
                                <input
                                  type="color"
                                  value={
                                    formik.values.punch_login_background_color
                                  }
                                  onChange={(e) =>
                                    formik.setFieldValue(
                                      "punch_login_background_color",
                                      e.target.value
                                    )
                                  }
                                />
                              </InputAdornment>
                            }
                            aria-describedby="outlined-color-helper-text"
                            inputProps={{
                              "aria-label": "punch_login_background_color",
                            }}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item lg={3} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">Font color</Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <FormControl
                          size="small"
                          variant="outlined"
                          sx={{
                            width: 200,
                            "& .MuiOutlinedInput-root": {
                              paddingRight: "8px",
                              fontSize: 14,
                            },
                            '& input[type="color"]': {
                              width: 24,
                              height: 24,
                              border: "none",
                              padding: 0,
                              background: "transparent",
                              cursor: "pointer",
                            },
                          }}
                        >
                          <OutlinedInput
                            id="outlined-adornment-color"
                            value={formik.values.punch_login_font_color}
                            onChange={(e) =>
                              formik.setFieldValue(
                                "punch_login_background_color",
                                e.target.value
                              )
                            }
                            endAdornment={
                              <InputAdornment position="end" sx={{ ml: 1 }}>
                                <input
                                  type="color"
                                  value={formik.values.punch_login_font_color}
                                  onChange={(e) =>
                                    formik.setFieldValue(
                                      "punch_login_font_color",
                                      e.target.value
                                    )
                                  }
                                />
                              </InputAdornment>
                            }
                            aria-describedby="outlined-color-helper-text"
                            inputProps={{
                              "aria-label": "punch_login_font_color",
                            }}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                          Background Image
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Box className={classes.imgBox}>
                          {selectedImagePunch && (
                            <img
                              src={URL.createObjectURL(selectedImagePunch)}
                              alt="Uploaded img"
                              className={classes.SelectImageBox}
                            />
                          )}
                          {!selectedImagePunch &&
                          initialData &&
                          initialData.punch_login_background_url ? (
                            <img
                              alt="Selected img"
                              height="160px"
                              src={initialData.punch_login_background_url}
                              style={{ position: "inherit" }}
                              className={classes.SelectImageBox}
                            />
                          ) : (
                            <></>
                          )}
                          <div>
                            <input
                              accept="image/*"
                              style={{ display: "none" }}
                              id="icon-button-file-up-bg_punch"
                              type="file"
                              onChange={handleImageChangePunch}
                            />
                            <div
                              style={{
                                textAlign: "center",
                              }}
                            >
                              <label htmlFor="icon-button-file-up-bg_punch">
                                <IconButton
                                  color="primary"
                                  aria-label="upload picture up bg"
                                  component="span"
                                >
                                  <CloudUploadIcon sx={{ fontSize: 45 }} />
                                </IconButton>
                              </label>
                              <div>PNG, JPG and JPEG are allowed</div>
                            </div>
                          </div>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Typography className={classes.cardTitle}>
                      Set Slider Items
                    </Typography>
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12} pb={5}>
                    <Grid
                      container
                      direction="row"
                      alignItems="center"
                      spacing={2}
                    >
                      <TableContainer>
                        <Table className={classes.table}>
                          <TableHead className={classes.tableHeaderRow}>
                            <TableRow>
                              <TableCell
                                className={classes.tablheaderId}
                                sx={{ width: "10%" }}
                              >
                                Type
                              </TableCell>
                              <TableCell
                                className={classes.tablheaderId}
                                sx={{ width: "85%" }}
                              >
                                Add Image / Video
                              </TableCell>
                              <TableCell
                                className={classes.tablheaderId}
                                sx={{ width: "5%", textAlign: "end" }}
                              >
                                Action
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {isLoading ? (
                              <TableRow>
                                <TableCell colSpan={20} align="center">
                                  <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    height="10vh"
                                  >
                                    <CircularProgress />
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ) : isError || punchSlideItems.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={20} align="center">
                                  {isError
                                    ? "Error fetching data"
                                    : "No data available"}
                                </TableCell>
                              </TableRow>
                            ) : (
                              punchSlideItems &&
                              punchSlideItems.map((row, index) => {
                                return (
                                  <TableRow key={index}>
                                    <TableCell
                                      className={classes.tablCellId}
                                      sx={{ width: "10%" }}
                                    >
                                      <Autocomplete
                                        options={Object.values(
                                          punchSliderItems
                                        )}
                                        value={row.type || ""}
                                        onChange={(event, newValue) =>
                                          handleFieldChangePB(
                                            index,
                                            "type",
                                            newValue || ""
                                          )
                                        }
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            placeholder="type.."
                                            size="small"
                                            className={classes.textField}
                                            InputProps={{
                                              ...params.InputProps,
                                              sx: {
                                                fontSize: 14,
                                                border: "none",
                                                minWidth: "150px",
                                              },
                                              classes: {
                                                root: classes.textFieldQuote23,
                                                notchedOutline:
                                                  classes.notchedOutline,
                                              },
                                            }}
                                            InputLabelProps={{
                                              sx: {
                                                fontSize: 14,
                                                border: "none",
                                              },
                                            }}
                                          />
                                        )}
                                        sx={{ width: "200px" }}
                                        size="small"
                                        className={classes.searchTextField23}
                                      />
                                    </TableCell>
                                    <TableCell
                                      className={classes.tablCellId}
                                      sx={{ width: "85%", minWidth: 500 }}
                                    >
                                      <Grid container spacing={1}>
                                        <Grid
                                          item
                                          lg={12}
                                          md={12}
                                          sm={12}
                                          xs={12}
                                        >
                                          {row.type === "image" ? (
                                            <Box className={classes.imgBox}>
                                              {row.url && (
                                                <img
                                                  src={row.url}
                                                  alt="Uploaded preview"
                                                  className={
                                                    classes.SelectImageBox
                                                  }
                                                />
                                              )}
                                              <div>
                                                <input
                                                  type="file"
                                                  accept="image/*"
                                                  id={`upload-${index}`}
                                                  style={{ display: "none" }}
                                                  onChange={(e) =>
                                                    handleFieldChangeImg(
                                                      index,
                                                      e
                                                    )
                                                  }
                                                  className={
                                                    classes.SelectImageBox
                                                  }
                                                />
                                                <div
                                                  style={{
                                                    textAlign: "center",
                                                  }}
                                                >
                                                  <label
                                                    htmlFor={`upload-${index}`}
                                                  >
                                                    <IconButton
                                                      color="primary"
                                                      component="span"
                                                    >
                                                      <CloudUploadIcon
                                                        sx={{ fontSize: 45 }}
                                                      />
                                                    </IconButton>
                                                  </label>
                                                  <div>
                                                    PNG, JPG and JPEG are
                                                    allowed
                                                  </div>
                                                </div>
                                              </div>
                                            </Box>
                                          ) : row.type === "video" ? (
                                            <TextField
                                              placeholder="Video URL"
                                              size="small"
                                              value={row.url}
                                              onChange={(e) =>
                                                handleFieldChangePB(
                                                  index,
                                                  "url",
                                                  e.target.value
                                                )
                                              }
                                              className={classes.textField}
                                              InputProps={{
                                                sx: {
                                                  fontSize: 14,
                                                },
                                              }}
                                              InputLabelProps={{
                                                sx: {
                                                  fontSize: 14,
                                                },
                                              }}
                                            />
                                          ) : null}
                                        </Grid>
                                      </Grid>
                                    </TableCell>
                                    <TableCell
                                      className={classes.tablCellId}
                                      sx={{ width: "5%", textAlign: "end" }}
                                    >
                                      <Grid
                                        container
                                        spacing={1}
                                        sx={{
                                          display: "flex",
                                          justifyContent: "flex-end",
                                        }}
                                      >
                                        <Grid item>
                                          <IconButton
                                            color="error"
                                            onClick={() =>
                                              handleDeleteRowPB(index)
                                            }
                                          >
                                            <FaTrash />
                                          </IconButton>
                                        </Grid>
                                      </Grid>
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        py={2}
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                        }}
                      >
                        <Button
                          variant="contained"
                          className={classes.addButton}
                          onClick={handleAddRowPB}
                        >
                          Add New
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid container spacing={5}></Grid>
              </Card>
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ borderBottom: 2, padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Typography className={classes.cardTitle}>
                      Facial Login Setting
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={3} p={"24px"}>
                  <Grid item lg={3} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">Radius (m)</Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Radius (m)"
                          size="small"
                          {...formik.getFieldProps("calculate_radius")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item lg={3} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">Latitude</Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Company Location latitude"
                          size="small"
                          {...formik.getFieldProps("company_latitude")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item lg={3} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">Longitude</Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Company Location longitude"
                          size="small"
                          {...formik.getFieldProps("company_longitude")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid container spacing={3} p={"24px"}>
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    sx={{ display: "flex", justifyContent: "flex-end" }}
                  >
                    <Button
                      variant="contained"
                      startIcon={
                        settingLoading ? (
                          <CircularProgress
                            size={18}
                            className={classes.isLoading}
                          />
                        ) : (
                          <SaveAltIcon />
                        )
                      }
                      className={classes.addButton}
                      onClick={() => formik.handleSubmit()}
                      disabled={!formValid}
                    >
                      Save
                    </Button>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Grid>
  );
};

export default Index;
